import React, { useContext, useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRect } from '@reach/rect';
import styled, { css } from 'styled-components';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import { FlexiflowComponent } from '@flexiflow-ui-kit/system';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject$1, _templateObject2, _templateObject3;
var ModalOverlay = styled(DialogOverlay)(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  ", ";\n  && {\n    display: none;\n  }\n"])), function (p) {
  return p.theme.predefinedCss.borderBoxChildren;
});
var ModalContent = styled(DialogContent)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  && {\n    position: absolute;\n    padding: 0;\n    background-color: ", ";\n    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1));\n    border-radius: 4px;\n    width: auto;\n    height: auto;\n    margin: 4px 0;\n    outline: none;\n    z-index: 2;\n    ", ";\n    overflow: hidden;\n  }\n"])), function (p) {
  return p.theme.palette.white;
}, function (p) {
  return p.$offsetTop !== null && css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n        max-height: calc(100vh - ", "px - 5px);\n      "])), p.$offsetTop);
});

var DropdownContext = /*#__PURE__*/React.createContext({
  onClose: function onClose() {},
  isOpen: false,
  relativeContainerElement: null,
  noScreenOverflow: true
});

function Dropdown(_ref) {
  var children = _ref.children,
      className = _ref.className;

  var _useContext = useContext(DropdownContext),
      isOpen = _useContext.isOpen,
      onClose = _useContext.onClose,
      relativeContainerElement = _useContext.relativeContainerElement;

  var onEsc = useCallback(function (_ref2) {
    var code = _ref2.code;

    if (code === 'Escape') {
      onClose();
    }
  }, [onClose]);
  return /*#__PURE__*/React.createElement(ModalOverlay, {
    isOpen: isOpen,
    dangerouslyBypassFocusLock: true,
    onKeyDown: onEsc,
    dangerouslyBypassScrollLock: true
  }, relativeContainerElement && /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(DropdownContent, {
    className: className
  }, children), relativeContainerElement));
}

function DropdownContent(_ref3) {
  var children = _ref3.children,
      className = _ref3.className;

  var _useContext2 = useContext(DropdownContext),
      noScreenOverflow = _useContext2.noScreenOverflow;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      modalContentTop = _useState2[0],
      setModalContentTop = _useState2[1];

  var modalContentRef = useRef(null);
  useRect(modalContentRef, {
    observe: true,
    onChange: function onChange(_ref4) {
      var top = _ref4.top;
      return setModalContentTop(top);
    }
  });
  return /*#__PURE__*/React.createElement(ModalContent, {
    "aria-label": "modal",
    className: className,
    $offsetTop: noScreenOverflow ? modalContentTop : null,
    ref: modalContentRef
  }, children);
}

var _templateObject;
var RelativeContainer = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: relative;\n  flex-shrink: 0;\n"])));

function DropdownWrapper(_ref) {
  var children = _ref.children,
      isOpen = _ref.isOpen,
      onClose = _ref.onClose,
      className = _ref.className,
      _ref$noScreenOverflow = _ref.noScreenOverflow,
      noScreenOverflow = _ref$noScreenOverflow === void 0 ? true : _ref$noScreenOverflow;
  var relativeContainerRef = useRef(null);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      relativeContainerElement = _useState2[0],
      setRelativeContainerElement = _useState2[1];

  var contextValue = useMemo(function () {
    return {
      onClose: onClose,
      isOpen: isOpen,
      relativeContainerElement: relativeContainerElement,
      noScreenOverflow: noScreenOverflow
    };
  }, [onClose, isOpen, relativeContainerElement, noScreenOverflow]);
  var onEsc = useCallback(function (_ref2) {
    var code = _ref2.code;

    if (code === 'Escape') {
      onClose();
    }
  }, [onClose]);
  useEffect(function () {
    if (relativeContainerRef.current) {
      setRelativeContainerElement(relativeContainerRef.current);
    }
  }, []);
  useEffect(function () {
    var closeFilter = function closeFilter(evt) {
      if (!relativeContainerRef.current) {
        return;
      }

      if (relativeContainerRef.current.contains(evt.target)) {
        return;
      }

      onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', closeFilter);
      document.addEventListener('keypress', closeFilter);
    }

    return function () {
      document.removeEventListener('mousedown', closeFilter);
      document.removeEventListener('keypress', closeFilter);
    };
  }, [onClose, isOpen]);
  return /*#__PURE__*/React.createElement(FlexiflowComponent, null, /*#__PURE__*/React.createElement(RelativeContainer, {
    className: className,
    onKeyDown: onEsc,
    ref: relativeContainerRef
  }, /*#__PURE__*/React.createElement(DropdownContext.Provider, {
    value: contextValue
  }, children)));
}

export { Dropdown, DropdownWrapper };
